import React, { useState, useEffect } from 'react';
import { createUsuario, updateUsuario, Usuario, eliminarUsuario } from '../services/usuarioService';
import { hasUserManagementPermission } from '../services/authService';
import { Button, Card, CardHeader, Checkbox, FormField, Input } from './ui';
import { TrashIcon } from '@heroicons/react/24/outline';

interface UsuarioFormProps {
  usuario?: Usuario;
  onUsuarioSaved: () => void;
  onCancel: () => void;
}

const mensajeDeError = (err: any, alternativa: string): string =>
  err?.response?.data?.message || err?.message || alternativa;

const UsuarioForm: React.FC<UsuarioFormProps> = ({ usuario, onUsuarioSaved, onCancel }) => {
  const puedeGestionar = hasUserManagementPermission();

  const initialFormState: Usuario = {
    nombre: '',
    apellido: '',
    nom_usuario: '',
    email: '',
    pass: '',
    estado: 1,
    p_busqueda: false,
    p_carga: false,
    p_modif_analisis: false,
    p_nuevo_analisis: false,
    p_g_alertas: false,
    p_g_config: false,
    p_g_ensayos: false,
    p_g_usuarios: false
  };

  const [formData, setFormData] = useState<Usuario>({
    nombre: '',
    apellido: '',
    email: '',
    pass: '',
    nom_usuario: '',
    estado: 1,
    p_busqueda: false,
    p_carga: false,
    p_modif_analisis: false,
    p_nuevo_analisis: false,
    p_g_alertas: false,
    p_g_config: false,
    p_g_ensayos: false,
    p_g_usuarios: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (usuario) {
      setFormData({
        ...initialFormState,
        ...usuario
      });
    }
  }, [usuario]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'number') {
      setFormData({ ...formData, [name]: parseInt(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (usuario?.id) {
        // Modo edición - solo campos editables
        const updateData = {
          nombre: formData.nombre,
          apellido: formData.apellido,
          nom_usuario: formData.nom_usuario,
          email: formData.email,
          estado: formData.estado,
          p_busqueda: formData.p_busqueda,
          p_carga: formData.p_carga,
          p_modif_analisis: formData.p_modif_analisis,
          p_nuevo_analisis: formData.p_nuevo_analisis,
          p_g_alertas: formData.p_g_alertas,
          p_g_config: formData.p_g_config,
          p_g_ensayos: formData.p_g_ensayos,
          p_g_usuarios: formData.p_g_usuarios
        };
        await updateUsuario(usuario.id, updateData);
      } else {
        // Modo creación
        await createUsuario(formData);
      }

      onUsuarioSaved();
      setFormData(initialFormState);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo guardar el usuario.'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Elimina únicamente a esta persona. La organización a la que pertenece no se
   * toca: puede tener otras cuentas y análisis en curso. Para dar de baja al
   * cliente o al laboratorio completo está el botón de su ficha, en Gestión.
   *
   * La baja es lógica: el registro pasa a estado 2, deja de aparecer en la
   * aplicación y el historial de análisis conserva la referencia a su autor.
   */
  const handleEliminar = async () => {
    if (!usuario?.id) return;

    const confirmMessage =
      `¿Confirma que desea eliminar al usuario ${usuario.nombre} ${usuario.apellido || ''}?` +
      ' Dejará de aparecer en la aplicación y no podrá iniciar sesión.' +
      ' El historial de análisis se conserva y su organización no se modifica.';

    if (window.confirm(confirmMessage)) {
      setLoading(true);
      setError(null);

      try {
        await eliminarUsuario(usuario.id);
        onUsuarioSaved(); // Esto recargará la lista
      } catch (err) {
        setError(mensajeDeError(err, 'No se pudo eliminar el usuario.'));
      } finally {
        setLoading(false);
      }
    }
  };

  // Redirigir si no es administrador (movido aquí después de los hooks)
  if (!puedeGestionar) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-center">
        <strong>Acceso denegado:</strong> no cuenta con el permiso de gestión de usuarios.
        <div className="mt-4">
          <Button variant="secondary" onClick={onCancel}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  const fieldError = (field: string) => (error && error.includes(field) ? error : undefined);

  return (
    <Card>
      <CardHeader
        title={usuario?.id ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        action={
          usuario?.id && !!usuario.estado && (
            <Button
              variant="danger"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={handleEliminar}
              disabled={loading}
            >
              Eliminar usuario
            </Button>
          )
        }
      />

      {error && !['nombre', 'apellido', 'email', 'nom_usuario', 'pass'].some(f => error.includes(f)) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}


      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nombre" htmlFor="nombre" required error={fieldError('nombre')}>
            <Input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              hasError={!!fieldError('nombre')}
              placeholder="Nombre del usuario"
              required
            />
          </FormField>

          <FormField label="Apellido" htmlFor="apellido" error={fieldError('apellido')}>
            <Input
              type="text"
              id="apellido"
              name="apellido"
              value={formData.apellido || ''}
              onChange={handleChange}
              hasError={!!fieldError('apellido')}
              placeholder="Apellido del usuario"
            />
          </FormField>

          <FormField label="Email" htmlFor="email" required error={fieldError('email')}>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              hasError={!!fieldError('email')}
              placeholder="Email del usuario"
              required
            />
          </FormField>

          <FormField label="Nombre de Usuario" htmlFor="nom_usuario" error={fieldError('nom_usuario')}>
            <Input
              type="text"
              id="nom_usuario"
              name="nom_usuario"
              value={formData.nom_usuario || ''}
              onChange={handleChange}
              hasError={!!fieldError('nom_usuario')}
              placeholder="Nombre de usuario para login"
            />
          </FormField>

          {(!usuario || !usuario.id) && (
            <FormField label="Contraseña" htmlFor="pass" required={!usuario} error={fieldError('pass')}>
              <Input
                type="password"
                id="pass"
                name="pass"
                value={formData.pass || ''}
                onChange={handleChange}
                hasError={!!fieldError('pass')}
                placeholder="Contraseña del usuario"
                required={!usuario}
              />
            </FormField>
          )}

          <div>
            <span className="block text-sm font-medium text-slate-700 mb-1">Estado</span>
            <div className="mt-2">
              <Checkbox
                label="Usuario Activo"
                name="estado"
                checked={formData.estado === 1}
                onChange={(e) => setFormData({ ...formData, estado: e.target.checked ? 1 : 0 })}
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 border-b border-slate-200 pb-2 text-slate-900">Permisos del Usuario</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <Checkbox label="Permiso de Búsqueda" name="p_busqueda" checked={!!formData.p_busqueda} onChange={handleChange} />
            <Checkbox label="Permiso de Carga" name="p_carga" checked={!!formData.p_carga} onChange={handleChange} />
            <Checkbox label="Modificar análisis" name="p_modif_analisis" checked={!!formData.p_modif_analisis} onChange={handleChange} />
            <Checkbox label="Crear análisis" name="p_nuevo_analisis" checked={!!formData.p_nuevo_analisis} onChange={handleChange} />
            {usuario?.tabla_grupo === 'administradores' && <Checkbox label="Gestionar alertas" name="p_g_alertas" checked={!!formData.p_g_alertas} onChange={handleChange} />}
            {usuario?.tabla_grupo === 'administradores' && <Checkbox label="Gestionar configuración" name="p_g_config" checked={!!formData.p_g_config} onChange={handleChange} />}
            {usuario?.tabla_grupo === 'administradores' && <Checkbox label="Gestionar ensayos" name="p_g_ensayos" checked={!!formData.p_g_ensayos} onChange={handleChange} />}
            {usuario?.tabla_grupo === 'administradores' && <Checkbox label="Gestionar usuarios" name="p_g_usuarios" checked={!!formData.p_g_usuarios} onChange={handleChange} />}
          </div>
        </div>

        <div className="flex items-center justify-end space-x-4 mt-6">
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            {usuario?.id ? 'Actualizar' : 'Guardar'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default UsuarioForm;
